/**
 * The browser end of the continuous claim ledger (#170-A).
 *
 * A bundle queue and a ledger writer look similar and are not the same object. The
 * legacy `ClaimOutbox` holds *signed bundles*: independent, order-free, each safe to
 * drop on its own. This holds a **sequence**. Every delta carries `delta_sequence`
 * inside the bytes that were signed, sequences must be contiguous, and the server
 * refuses a gap. Three consequences drive the whole design:
 *
 * 1. **Sequences are allocated at the head, not at `push`.** A draft waits unsigned.
 *    It is signed only when it reaches the front of the queue, at `lastAcked + 1`, and
 *    the next sequence is not allocated until that one comes back `accepted` or
 *    `duplicate`. A rejected or *uncertain* write therefore burns no sequence -- which
 *    it would if signing happened at queue time, leaving a permanent hole nothing could
 *    fill.
 *
 * 2. **A timeout is not a retry.** Without an answer, "the write never landed" and "the
 *    write committed and the response was lost" are indistinguishable, and they need
 *    opposite actions. Guessing wrong either loses a delta or invents a second one. So
 *    an uncertain result is resolved by asking the server what it holds -- the `resume`
 *    handshake -- and the exact same signed envelope is replayed if it did not land.
 *    The ledger is idempotent on `(timeline_epoch, delta_id)`, so an exact replay of a
 *    write that *did* land answers `duplicate` rather than creating a row.
 *
 * 3. **Transport metadata is repaired; payloads never are.** The producer lease
 *    (`producer_id`/`fence`/`session_generation`) rides *outside* the signature. A
 *    takeover fences this writer out mid-flight; the fix is to re-read the lease and
 *    re-wrap the identical bytes. Re-signing to "fix" a transport error would be a
 *    different payload under the same delta id, which the ledger answers with an
 *    idempotency conflict -- and rightly.
 *
 * ## Two cursors, never one
 *
 * `writerAck` is *(sequence, digest)* of the last delta this device knows landed. The
 * session-feed cursor is the highest `session_sequence` this device has *read* off the
 * SSE feed. They are different numbers about different things -- the writer ACK is
 * per-device and per-epoch, the session sequence is session-wide across every device --
 * and substituting one for the other is silent data loss in both directions. An
 * accepted response carries `session_sequence`, and this class deliberately does not
 * use it to advance the read cursor: what this device *wrote* is not what it has *read*.
 *
 * ## Backpressure that never drops
 *
 * The queue is bounded by item count and by bytes. When it is full the answer is to
 * stop *harvesting* -- `canAccept` is asked before the scorer is drained, not after --
 * because the scorer's harvest is destructive and a rejected push would delete scored
 * windows that no longer exist anywhere. Nothing already queued is ever discarded to
 * make room.
 *
 * Everything is injected, so all of the above is testable without a network, a
 * microphone, or a clock.
 */

import { classifyClaimDeltaError } from "./claim-guard";
import type {
  ClaimDeltaAccepted,
  ClaimDeltaProducerContext,
  ClaimDeltaResumeRequest,
  ClaimDeltaResumeResponse,
  SignedClaimDelta as WireSignedClaimDelta,
} from "../contract/api";

/**
 * The producer lease quoted outside the signature, as the engine holds it.
 *
 * Advisory: the append path decides. A partial view of the generated
 * `ClaimDeltaProducerContext` -- the engine quotes only these three fields.
 */
export type ClaimDeltaContext = Pick<
  ClaimDeltaProducerContext,
  "producer_id" | "fence" | "session_generation"
>;

/** One harvest waiting for a sequence. A descriptor, deliberately not bytes. */
export interface ClaimDeltaDraft {
  /** Stable for this queued item across every retry and re-wrap. */
  deltaId: string;
  claimId: string;
  intervals: Array<{ start: number; end: number; confidence: number }>;
  envelopeBytes: Uint8Array | null;
  localStartSec: number;
  localEndSec: number;
  captureGaps: Array<{ start: number; end: number }>;
}

/**
 * The attested gaps intersecting one draft's span, in wire shape.
 *
 * Its own function so the slicing -- and the stripping -- can be asserted
 * without a microphone, a recorder, or a session: the recorder's gaps carry a
 * local `cause` for telemetry, and the server's `SpanWire` forbids extra keys,
 * so a gap that reaches a draft with its cause attached is a claim the ingest
 * rejects outright.
 */
export function sliceGapsForDraft(
  gaps: Array<{ start: number; end: number; cause?: string }>,
  from: number,
  to: number,
): Array<{ start: number; end: number }> {
  return gaps
    .filter((g) => g.end > from && g.start < to)
    .map((g) => ({ start: g.start, end: g.end }));
}

/**
 * Exact transmitted bytes plus the digest of those bytes.
 *
 * The generated `SignedClaimDelta` describes the wire body; the digest is the browser's
 * local identifier for what it sent, so it is added here rather than mirrored.
 */
export type SignedClaimDelta = Pick<WireSignedClaimDelta, "payload" | "signature"> & {
  /** Lower-case hex SHA-256 over the signed payload bytes, as `resume` identifies them. */
  digest: string;
};

/** The part of the committed ACK the outbox reads: a partial `ClaimDeltaAccepted`. */
export type ClaimDeltaAcceptedBody = Partial<ClaimDeltaAccepted>;

/** The part of the resume response the outbox reads: a partial `ClaimDeltaResumeResponse`. */
export type ClaimDeltaResumeBody = Partial<ClaimDeltaResumeResponse>;

/**
 * The wire body of one delta write: signed bytes inside, lease outside.
 *
 * The generated `SignedClaimDelta` already carries the producer fields; intersecting the
 * lease makes them required here, because this client always quotes them.
 */
export type ClaimDeltaRequest = WireSignedClaimDelta &
  Pick<ClaimDeltaProducerContext, "producer_id" | "fence" | "session_generation">;

export interface ClaimDeltaOutboxPorts {
  claimProfileId: string;
  /** One per recording. Sequences are scoped to it. */
  timelineEpoch: string;
  /** Build and sign one draft at an allocated sequence. Returns the exact bytes. */
  sign: (draft: ClaimDeltaDraft, sequence: number) => Promise<SignedClaimDelta>;
  post: (body: ClaimDeltaRequest) => Promise<ClaimDeltaAcceptedBody>;
  resume: (body: ClaimDeltaResumeRequest) => Promise<ClaimDeltaResumeBody>;
  /** Re-read the producer lease. Called on first use and after it is fenced out. */
  context: () => Promise<ClaimDeltaContext>;
  onAccepted: (body: ClaimDeltaAcceptedBody, draft: ClaimDeltaDraft) => void;
  /** Ours to retry; the head stays signed and put. `waiting` counts everything unsent. */
  onRetryable: (error: Error, waiting: number) => void;
  /** Refused forever. The writer stops; this is surfaced, never downgraded to legacy. */
  onPermanent: (error: Error, draft: ClaimDeltaDraft | null) => void;
  /** The session moved on. Nothing queued can ever land. */
  onTerminal: (error: Error) => void | Promise<void>;
  /** This device's token is dead. */
  onSignedOut: (error: Error) => void | Promise<void>;
  /** The queue filled up, so harvesting must pause. Reported once per fill. */
  onBackpressure?: (waiting: number, bytes: number) => void;
  maxItems?: number;
  maxBytes?: number;
  /** Injected so tests do not wait in real time. */
  sleep?: (ms: number) => Promise<void>;
}

/**
 * The protocol's maximum span for one delta, in seconds.
 *
 * Mirrors `MAX_CLAIM_DELTA_SPAN_SEC` in `meetfolks/protocol.py`. Stated here rather
 * than in `const.js` because it is a bound on this transport, not on the legacy claim
 * path -- and because the failure it prevents is unrecoverable: a span over the cap is
 * a 400 on a *signed* payload, which no retry and no lease refresh can repair. The
 * caller splits a long harvest before signing; see `session.ts`.
 */
export const MAX_CLAIM_DELTA_SPAN_SEC = 60;

/**
 * What one delta is actually allowed to cover, with headroom under the cap.
 *
 * [D] 55 s, not 60. Harvest boundaries land on window ends rather than on a round
 * number, and `local_end_sec` is rounded to three places, so splitting exactly at the
 * ceiling would put a fraction of a second between "correct" and "permanently refused".
 */
export const CLAIM_DELTA_SPLIT_SEC = 55;

/**
 * Item and byte bounds.
 *
 * [D] 32 items and 1 MiB. A delta is a few kB, so the byte bound is the one that binds
 * on a session with dense intervals and the item bound is the one that binds on a stalled
 * uplink. Both exist because either alone has an unbounded other axis: 32 items says
 * nothing about memory if each is at the server's 64 kB payload ceiling, and 1 MiB says
 * nothing about the retry backlog if each is tiny. The number that matters is neither
 * bound but what happens at it -- scoring pauses, and no signed claim is discarded.
 */
const DEFAULT_MAX_ITEMS = 32;
const DEFAULT_MAX_BYTES = 1024 * 1024;

/**
 * Lease refreshes spent on one head before giving up for this pass.
 *
 * [D] Two. A single takeover needs one refresh. Needing three in one pass means the
 * scope is being contested faster than this writer can quote it, and spinning on the
 * append route is worse than holding the head and letting the next tick try.
 */
const MAX_CONTEXT_REFRESHES = 2;

/**
 * Consecutive resume refusals before the writer gives up.
 *
 * [D] Three. The resume route raises `sequence_gap` from two independent checks, only
 * one of which is about this writer, so a single refusal is worth holding through. A
 * third in a row means the handshake is not going to answer, and a queue that holds
 * forever while reporting "waiting to upload" is a silent stall.
 */
const MAX_RESUME_CONFLICTS = 3;

/** Copy an error's transport fields onto a clearer message. */
function annotate(source: Error & { status?: number; data?: unknown }, message: string): Error {
  return Object.assign(new Error(message), { status: source.status, data: source.data });
}

function aheadWaterError(sequence: number, highWater: number, epoch: string): Error {
  return Object.assign(
    new Error(
      `the server holds delta sequence ${highWater} for timeline ${epoch}, past the ${sequence} ` +
        "this device was writing; those rows were never matched or replayed, so this " +
        "writer cannot continue without inventing a sequence"
    ),
    { reason: "ahead_water" }
  );
}

function behindWaterError(acked: number, highWater: number, epoch: string): Error {
  return Object.assign(
    new Error(
      `the server holds delta sequence ${highWater} for timeline ${epoch}, behind the ${acked} ` +
        "it previously acknowledged to this device; writing on would leave a gap"
    ),
    { reason: "behind_water" }
  );
}

const realSleep = (ms: number) => new Promise<void>((r) => setTimeout(r, ms));

/** Rough wire cost of a draft, used only for the byte bound. */
export function draftBytes(draft: ClaimDeltaDraft): number {
  // ~48 bytes per interval on the wire once serialised, plus the envelope (base64 is
  // 4 bytes per 3), plus a fixed header allowance for the identifiers and timestamps.
  const envelope = draft.envelopeBytes ? Math.ceil(draft.envelopeBytes.length / 3) * 4 : 0;
  return 512 + draft.intervals.length * 48 + draft.captureGaps.length * 40 + envelope;
}

interface Head {
  draft: ClaimDeltaDraft;
  sequence: number;
  signed: SignedClaimDelta;
  bytes: number;
}

export class ClaimDeltaOutbox {
  private readonly queue: ClaimDeltaDraft[] = [];
  private queuedBytes = 0;
  /** Signed, byte-fixed, and in flight or awaiting retry. Never re-signed. */
  private head: Head | null = null;
  /** The current pass, so a second `flush()` awaits it rather than returning early. */
  private runner: Promise<void> | null = null;
  private closed = false;
  private context: ClaimDeltaContext | null = null;
  private contextRefreshes = 0;
  /** Consecutive refusals of the resume handshake itself. Reset by any answer. */
  private resumeConflicts = 0;
  private backpressured = false;
  /**
   * Whether a network call this class made is still awaiting its answer (#170-B).
   *
   * Counted, not a boolean, so a `resume` issued while reconciling a head cannot clear
   * the flag the head's own post set. `length` alone is not enough for the wrap-up gate:
   * a head is nulled at the moment it is acknowledged, so there is a window in which
   * nothing is queued and a request is still open.
   */
  private requestsInFlight = 0;

  /** The writer ACK: what this device knows the server committed for it. */
  private ackedSequence = 0;
  private ackedDigest: string | null = null;
  /** The read cursor: the highest session-feed sequence this device has *seen*. */
  private seenSessionSequence = 0;

  private readonly maxItems: number;
  private readonly maxBytes: number;

  constructor(private readonly ports: ClaimDeltaOutboxPorts) {
    this.maxItems = ports.maxItems ?? DEFAULT_MAX_ITEMS;
    this.maxBytes = ports.maxBytes ?? DEFAULT_MAX_BYTES;
  }

  /** Everything not yet acknowledged, head included. */
  get length(): number {
    return this.queue.length + (this.head ? 1 : 0);
  }

  get bytes(): number {
    return this.queuedBytes + (this.head?.bytes ?? 0);
  }

  get isClosed(): boolean {
    return this.closed;
  }

  /**
   * The writer ACK. Reported so the wrap-up seam can *see* it; nothing in this slice
   * treats it as durable finality -- that is #170-B's watermark, and an in-memory value
   * is not a promise that a device finished.
   */
  get writerAck(): { sequence: number; digest: string | null } {
    return { sequence: this.ackedSequence, digest: this.ackedDigest };
  }

  /**
   * Whether a request this writer issued is still outstanding (#170-B).
   *
   * [V] Defence in depth, not the load-bearing condition, and worth saying so: `length`
   * counts the head, and the head is non-null for the whole duration of its post and its
   * reconciling `resume`, so today it already excludes every in-flight window. Reverting
   * `isSettled` to `length === 0` alone passes this file's whole suite.
   *
   * Kept because the thing it guards against is a *caller* change rather than a bug here:
   * anything that pushes into the queue while a drain is polling would otherwise be able
   * to slip a delta past a drain that had already reported success, and the marker read
   * off that drain is checked against the ledger head for exact equality. The condition
   * costs a comparison; the failure it forecloses is an unprovable finality.
   */
  get hasInFlight(): boolean {
    return this.requestsInFlight > 0;
  }

  /** Whether a flush pass is running. A pass can still sign and send another head. */
  get isRunning(): boolean {
    return this.runner !== null;
  }

  /**
   * Whether this writer is quiet: nothing queued, no head, no pass, no open request.
   *
   * The four conditions are the definition of "every delta has been acknowledged", and
   * all four are load-bearing -- an empty queue with a signed head is a delta that has
   * not been sent, and an empty queue with a running pass is one that may yet be.
   */
  get isSettled(): boolean {
    return this.length === 0 && !this.isRunning && !this.hasInFlight;
  }

  /**
   * The ACK pair to publish as a final watermark, or `null` if it is not safe to read.
   *
   * Returns `null` rather than a best guess whenever the writer is not settled: the
   * marker is checked against the ledger head for exact equality, so a pair read while a
   * delta is still in flight is not a weaker claim, it is a wrong one.
   *
   * Frozen, because the whole retry contract depends on the body not changing between
   * attempts. Handing back a mutable object would make that a convention rather than a
   * property.
   */
  finalAck(): Readonly<{ sequence: number; digest: string | null }> | null {
    if (!this.isSettled) return null;
    const { sequence, digest } = this.writerAck;
    // The server's own invariant, checked here so a malformed pair is caught at the
    // device that computed it rather than as a 422 on the marker.
    if (sequence === 0) {
      if (digest !== null) return null;
    } else if (!/^[0-9a-f]{64}$/.test(digest ?? "")) {
      return null;
    }
    return Object.freeze({ sequence, digest });
  }

  /**
   * Seed the lease read while deciding whether to enter delta mode.
   *
   * Saves one round trip and nothing else: it is the same advisory value `context()`
   * would return, and it is discarded the moment the append route says it is stale.
   */
  primeContext(context: ClaimDeltaContext): void {
    this.context = context;
  }

  /** The session-feed read cursor. Never derived from an ACK. */
  get sessionCursor(): number {
    return this.seenSessionSequence;
  }

  /**
   * Record how far this device has read the unified session feed.
   *
   * Monotonic, and the only writer of this cursor. It is passed to `resume` alongside
   * the writer ACK precisely so the server can serve replay from the right place
   * without either number standing in for the other.
   */
  noteSessionSequence(sequence: number): void {
    if (Number.isFinite(sequence) && sequence > this.seenSessionSequence) {
      this.seenSessionSequence = sequence;
    }
  }

  /**
   * Whether one more harvest can be queued.
   *
   * Asked *before* the scorer is drained. `harvest()` is destructive -- it clears the
   * windows it returns -- so discovering there is no room after harvesting would mean
   * deleting scored speech, which is exactly what a bounded queue must not cost.
   */
  canAccept(bytes = 0): boolean {
    if (this.closed) return false;
    return this.length < this.maxItems && this.bytes + bytes <= this.maxBytes;
  }

  /**
   * Queue a harvest and kick a flush. Returns whether it was taken.
   *
   * A `false` here means the caller ignored `canAccept`; nothing already queued is
   * dropped to make room, and the refused draft is the caller's to hold or re-derive.
   */
  push(draft: ClaimDeltaDraft): boolean {
    if (this.closed) return false;
    const bytes = draftBytes(draft);
    if (!this.canAccept(bytes)) {
      this.reportBackpressure();
      return false;
    }
    this.queue.push(draft);
    this.queuedBytes += bytes;
    if (!this.canAccept()) this.reportBackpressure();
    else this.backpressured = false;
    void this.flush();
    return true;
  }

  private reportBackpressure(): void {
    if (this.backpressured) return;
    this.backpressured = true;
    this.ports.onBackpressure?.(this.length, this.bytes);
  }

  /**
   * Post everything queued, oldest first, one at a time and strictly in order.
   *
   * Serial is not a performance choice here: sequences are contiguous and each one is
   * allocated from the previous ACK, so there is never more than one delta in flight.
   */
  flush(): Promise<void> {
    if (this.closed) return Promise.resolve();
    // Returns the pass already running rather than a resolved promise. `push` kicks a
    // flush of its own, so a caller that awaited a no-op would be told the queue had
    // been attempted when it had not -- which is precisely the confusion the wrap-up
    // drain must not be under.
    if (this.runner) return this.runner;
    this.runner = this.pass().finally(() => {
      this.runner = null;
    });
    return this.runner;
  }

  private async pass(): Promise<void> {
    while (!this.closed && (this.head || this.queue.length)) {
      if (!this.head && !(await this.signNextHead())) return;
      if (!(await this.attemptHead())) return;
    }
  }

  /** Take the oldest draft and bind it to `lastAcked + 1`. Bytes are fixed from here. */
  private async signNextHead(): Promise<boolean> {
    const draft = this.queue[0];
    const sequence = this.ackedSequence + 1;
    let signed: SignedClaimDelta;
    try {
      signed = await this.ports.sign(draft, sequence);
    } catch (raw) {
      // Signing is local. A failure here is a bug or a dead key, not a network
      // condition, and retrying it forever would stall the queue in silence.
      this.closed = true;
      this.ports.onPermanent(raw as Error, draft);
      return false;
    }
    this.queue.shift();
    this.queuedBytes -= draftBytes(draft);
    if (this.queuedBytes < 0) this.queuedBytes = 0;
    this.head = { draft, sequence, signed, bytes: draftBytes(draft) };
    this.contextRefreshes = 0;
    return true;
  }

  /** One attempt at the current head. `false` means "held; try again next tick". */
  private async attemptHead(): Promise<boolean> {
    const head = this.head;
    if (!head) return true;

    if (!this.context && !(await this.acquireContext())) return false;
    const context = this.context as ClaimDeltaContext;

    this.requestsInFlight += 1;
    try {
      const accepted = await this.ports.post({
        claim_profile_id: this.ports.claimProfileId,
        payload: head.signed.payload,
        signature: head.signed.signature,
        algorithm: "ed25519",
        producer_id: context.producer_id,
        fence: context.fence,
        session_generation: context.session_generation,
      });
      this.acknowledge(head, accepted);
      return true;
    } catch (raw) {
      return await this.handleFailure(head, raw as Error);
    } finally {
      this.requestsInFlight -= 1;
    }
  }

  private async acquireContext(): Promise<boolean> {
    try {
      this.context = await this.ports.context();
      return true;
    } catch (raw) {
      const error = raw as Error & { status?: number };
      const verdict = classifyClaimDeltaError(error);
      if (verdict === "signed-out") {
        this.closed = true;
        void this.ports.onSignedOut(error);
        return false;
      }
      if (verdict === "terminal") {
        this.closed = true;
        void this.ports.onTerminal(error);
        return false;
      }
      // Everything else -- no producer yet, a network blip, a 5xx -- holds the signed
      // queue exactly where it is. The one thing that must not happen is falling back
      // to the legacy payload protocol mid-session: the sequence already started.
      this.ports.onRetryable(error, this.length);
      return false;
    }
  }

  /**
   * Take an accepted or duplicate answer as the ACK for this head.
   *
   * `duplicate` advances the ACK identically to `accepted`, and must: it is the answer
   * an exact replay of an already-committed write receives, which is precisely the
   * outcome the uncertain path is engineered to produce.
   */
  private acknowledge(head: Head, body: ClaimDeltaAcceptedBody): void {
    this.ackedSequence = Math.max(this.ackedSequence, head.sequence);
    this.ackedDigest = head.signed.digest;
    this.head = null;
    this.contextRefreshes = 0;
    this.resumeConflicts = 0;
    this.backpressured = false;
    // `body.session_sequence` is deliberately not folded into `seenSessionSequence`.
    // What this device wrote is not what it has read.
    this.ports.onAccepted(body, head.draft);
  }

  private async handleFailure(head: Head, error: Error & { status?: number }): Promise<boolean> {
    switch (classifyClaimDeltaError(error)) {
      case "signed-out":
        this.closed = true;
        await this.ports.onSignedOut(error);
        return false;

      case "terminal":
        this.closed = true;
        await this.ports.onTerminal(error);
        return false;

      case "permanent":
        // A gap, an idempotency conflict, a bad signature, a participant mismatch: all
        // judgements about bytes that are already fixed. Allocating a new sequence
        // would "fix" nothing and would corrupt the ledger's contiguity.
        this.closed = true;
        this.ports.onPermanent(error, head.draft);
        return false;

      case "stale-context":
        this.context = null;
        this.contextRefreshes += 1;
        if (this.contextRefreshes > MAX_CONTEXT_REFRESHES) {
          this.ports.onRetryable(error, this.length);
          return false;
        }
        // Same bytes, new envelope. The loop reposts this identical head.
        return true;

      case "uncertain":
        return await this.reconcile(head, error);

      default:
        this.ports.onRetryable(error, this.length);
        return false;
    }
  }

  /**
   * Resolve an answer this device never saw, by asking what the server holds.
   *
   * The high-water mark is the whole decision. At or past this head's sequence, the
   * write committed and the lost response was an ACK; below it, nothing landed and the
   * identical envelope is replayed. No new sequence is allocated on either branch.
   */
  private async reconcile(head: Head, error: Error): Promise<boolean> {
    let state: ClaimDeltaResumeBody;
    this.requestsInFlight += 1;
    try {
      state = await this.ports.resume(this.resumeRequest());
    } catch (raw) {
      const resumeError = raw as Error & { status?: number };
      const verdict = classifyClaimDeltaError(resumeError);
      if (verdict === "signed-out") {
        this.closed = true;
        await this.ports.onSignedOut(resumeError);
        return false;
      }
      if (verdict === "terminal") {
        this.closed = true;
        await this.ports.onTerminal(resumeError);
        return false;
      }
      // A 409 from the *handshake* is deliberately not permanent, and this is the one
      // place the two cursors could have been quietly collapsed. The resume route raises
      // `sequence_gap` from two independent checks -- one on the writer ACK and one on
      // `last_seen_session_sequence` -- so a read-cursor complaint arriving here would
      // otherwise kill the write path for the rest of the session. The head's fate is
      // still unknown either way, and asking again is idempotent, so it holds.
      //
      // The append route's 409 is a different thing and is still permanent: there it is
      // a statement about bytes that are already fixed. See `handleFailure`.
      if (verdict === "permanent" && resumeError.status !== 409) {
        // A 410 `snapshot_required` is not something a writer can repair by writing more.
        this.closed = true;
        this.ports.onPermanent(resumeError, head.draft);
        return false;
      }
      if (resumeError.status === 409) {
        // Held, but not forever. Holding is right for one or two passes -- the head's
        // fate is genuinely unknown and asking again is idempotent -- and wrong
        // indefinitely, because a queue that never moves while reporting "waiting to
        // upload" is the silent stall this whole class exists to avoid.
        this.resumeConflicts += 1;
        if (this.resumeConflicts >= MAX_RESUME_CONFLICTS) {
          this.closed = true;
          this.ports.onPermanent(
            annotate(
              resumeError,
              `the resume handshake refused ${this.resumeConflicts} times in a row ` +
                `(${resumeError.message}); this device cannot establish what the server ` +
                "holds and will not guess"
            ),
            head.draft
          );
          return false;
        }
      }
      // Could not ask. The head stays exactly as it is, still uncertain, and the next
      // tick asks again -- which is safe because replay is idempotent either way.
      this.ports.onRetryable(error, this.length);
      return false;
    } finally {
      this.requestsInFlight -= 1;
    }
    this.resumeConflicts = 0;

    const highWater = typeof state.high_water === "number" ? state.high_water : 0;

    // Three answers, not two, and collapsing the outer two is a live sequence gap.
    //
    // `head.sequence` is always `ackedSequence + 1` -- it is allocated there and the ACK
    // only moves when a head retires -- so the server's high-water can sit in exactly
    // three places relative to it, and only the middle one is ordinary.

    if (highWater > head.sequence) {
      // **Ahead.** The ledger holds sequences past the one this device believes it is
      // writing. Treating that as "it landed" -- which is what `>=` did -- ACKs locally
      // at `head.sequence` and then allocates `head.sequence + 1`, while the server is
      // expecting `highWater + 1`. Every subsequent write is then a `sequence_gap`, and
      // the rows in between were never matched or replayed, so nothing here knows what
      // they contain.
      //
      // There is no safe local repair. Skipping the ACK forward would claim rows this
      // device cannot identify and would leave `last_acked_digest` describing a delta
      // the server does not hold at that sequence, which fails every later resume.
      // Replaying is worse: the same `delta_id` under different bytes is an idempotency
      // conflict. So it fails closed and says why.
      this.closed = true;
      this.ports.onPermanent(
        aheadWaterError(head.sequence, highWater, this.ports.timelineEpoch),
        head.draft
      );
      return false;
    }

    if (highWater < this.ackedSequence) {
      // **Behind this device's own ACK.** The server holds less than it once told this
      // device it had committed -- a purge past the replay floor, or a different ledger
      // entirely. Writing on would create a gap at the first missing sequence.
      this.closed = true;
      this.ports.onPermanent(
        behindWaterError(this.ackedSequence, highWater, this.ports.timelineEpoch),
        head.draft
      );
      return false;
    }

    if (highWater === head.sequence) {
      // **Exactly one ahead of the ACK.** The write committed and the answer was lost.
      // Report it as the duplicate it effectively is, so a caller counting accepted
      // deltas is not told this one was new.
      this.acknowledge(head, {
        status: "duplicate",
        delta_sequence: head.sequence,
        high_water: highWater,
        next_expected_delta_sequence: highWater + 1,
      });
      return true;
    }

    // **Equal to the ACK.** It did not land. Same envelope, next pass.
    this.ports.onRetryable(error, this.length);
    return false;
  }

  /** The resume body, carrying both cursors and never conflating them. */
  private resumeRequest(): ClaimDeltaResumeRequest {
    const body: ClaimDeltaResumeRequest = {
      timeline_epoch: this.ports.timelineEpoch,
      last_acked_delta_sequence: this.ackedSequence,
      last_seen_session_sequence: this.seenSessionSequence,
    };
    // The route rejects a digest alongside a zero sequence, and rightly: there is no
    // committed row for it to identify.
    if (this.ackedSequence > 0 && this.ackedDigest) body.last_acked_digest = this.ackedDigest;
    return body;
  }

  /**
   * Wait for everything to be acknowledged, bounded. Returns whether it emptied.
   *
   * Polls rather than awaiting `flush`, because a flush may already be running from a
   * tick and a second call would return having done nothing.
   */
  async drain(timeoutMs = 45000, now: () => number = Date.now): Promise<boolean> {
    const sleep = this.ports.sleep ?? realSleep;
    const deadline = now() + timeoutMs;
    // `isSettled`, not `length` (#170-B). The wrap-up reads the final ACK off the back of
    // this answer and publishes it as durable finality, so "drained" has to mean nothing
    // can still change it -- a stricter predicate than "nothing is queued right now".
    // See `hasInFlight` for what the extra conditions do and do not currently catch.
    while (!this.isSettled && !this.closed && now() < deadline) {
      void this.flush();
      await sleep(200);
    }
    return this.isSettled;
  }
}
